/**
 * useJobs - Hooks for job operations
 * Feature 009: Workshop Booking & Job-Tracking System
 *
 * T051: useJobs hook for job fetching and operations
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { JobBoardItem } from '../components/jobs/JobBoard';
import type { JobDetailData } from '../components/jobs/JobDetail';
import type { TimelineEventData } from '../components/jobs/JobTimeline';

/**
 * Options for useJobs hook
 */
interface UseJobsOptions {
  status?: string;
  bayId?: string;
  staffId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * Return type for useJobs hook
 */
interface UseJobsReturn {
  jobs: JobBoardItem[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook for fetching jobs list
 */
export function useJobs(options: UseJobsOptions = {}): UseJobsReturn {
  const { status, bayId, staffId, search, limit = 100, offset = 0 } = options;

  const queryKey = ['jobs', status, bayId, staffId, search, limit, offset];

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (bayId) params.append('bayId', bayId);
      if (staffId) params.append('staffId', staffId);
      if (search) params.append('search', search);
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      // api.get<T> returns T directly, throws ApiError on failure
      return await api.get<{
        jobs: JobBoardItem[];
        total: number;
        limit: number;
        offset: number;
      }>(`/api/jobs?${params.toString()}`);
    },
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });

  return {
    jobs: data?.jobs || [],
    total: data?.total || 0,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Return type for useJobDetail hook
 */
interface UseJobDetailReturn {
  job: JobDetailData | null;
  timeline: TimelineEventData[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook for fetching single job detail with timeline
 */
export function useJobDetail(jobId: string | null): UseJobDetailReturn {
  const queryKey = ['job', jobId];

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!jobId) return null;

      // api.get<T> returns T directly, throws ApiError on failure
      return await api.get<JobDetailData & { timeline: TimelineEventData[] }>(`/api/jobs/${jobId}`);
    },
    enabled: !!jobId,
    staleTime: 10000,
  });

  return {
    job: data || null,
    timeline: data?.timeline || [],
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Return type for useJobTransition hook
 */
interface UseJobTransitionReturn {
  transition: (
    jobId: string,
    newStatus: string,
    notes?: string,
    transitionId?: string,
    transitionData?: {
      notes?: string;
      publicNote?: string;
      bayId?: string;
      estimateLines?: Array<{
        description: string;
        type: 'labor' | 'parts' | 'materials' | 'other';
        quantity: number;
        unitPrice: number;
        total: number;
      }>;
      customFields?: Record<string, unknown>;
    },
    checklistValues?: Array<{ itemKey: string; value: boolean | number | string | Record<string, unknown> | null }>
  ) => Promise<void>;
  isTransitioning: boolean;
  error: Error | null;
}

/**
 * Hook for transitioning job state
 */
export function useJobTransition(): UseJobTransitionReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      jobId,
      newStatus,
      notes,
      transitionId,
      transitionData,
      checklistValues,
    }: {
      jobId: string;
      newStatus: string;
      notes?: string;
      transitionId?: string;
      transitionData?: {
        notes?: string;
        publicNote?: string;
        bayId?: string;
        estimateLines?: Array<{
          description: string;
          type: 'labor' | 'parts' | 'materials' | 'other';
          quantity: number;
          unitPrice: number;
          total: number;
        }>;
        customFields?: Record<string, unknown>;
      };
      checklistValues?: Array<{ itemKey: string; value: boolean | number | string | Record<string, unknown> | null }>;
    }) => {
      // api.post<T> returns T directly, throws ApiError on failure
      return await api.post<{ job: JobDetailData }>(`/api/jobs/${jobId}/transition`, {
        newStatus,
        notes,
        transitionId,
        transitionData,
        checklistValues,
      });
    },
    onSuccess: () => {
      // Invalidate jobs list and job detail
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job'] });
    },
  });

  const transition = useCallback(
    async (
      jobId: string,
      newStatus: string,
      notes?: string,
      transitionId?: string,
      transitionData?: {
        notes?: string;
        publicNote?: string;
        bayId?: string;
        estimateLines?: Array<{
          description: string;
          type: 'labor' | 'parts' | 'materials' | 'other';
          quantity: number;
          unitPrice: number;
          total: number;
        }>;
        customFields?: Record<string, unknown>;
      },
      checklistValues?: Array<{ itemKey: string; value: boolean | number | string | Record<string, unknown> | null }>
    ) => {
      await mutation.mutateAsync({ jobId, newStatus, notes, transitionId, transitionData, checklistValues });
    },
    [mutation]
  );

  return {
    transition,
    isTransitioning: mutation.isPending,
    error: mutation.error as Error | null,
  };
}

/**
 * Return type for useJobEstimate hook
 */
interface UseJobEstimateReturn {
  addEstimate: (jobId: string, amount: number, notes: string) => Promise<void>;
  isAdding: boolean;
  error: Error | null;
}

/**
 * Hook for adding job estimate
 */
export function useJobEstimate(): UseJobEstimateReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      jobId,
      amount,
      notes,
    }: {
      jobId: string;
      amount: number;
      notes: string;
    }) => {
      // api.post<T> returns T directly, throws ApiError on failure
      return await api.post<{ job: JobDetailData }>(`/api/jobs/${jobId}/estimate`, {
        amount,
        notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  const addEstimate = useCallback(
    async (jobId: string, amount: number, notes: string) => {
      await mutation.mutateAsync({ jobId, amount, notes });
    },
    [mutation]
  );

  return {
    addEstimate,
    isAdding: mutation.isPending,
    error: mutation.error as Error | null,
  };
}

/**
 * Return type for useJobNote hook
 */
interface UseJobNoteReturn {
  addNote: (jobId: string, note: string) => Promise<void>;
  isAdding: boolean;
  error: Error | null;
}

/**
 * Hook for adding job note
 */
export function useJobNote(): UseJobNoteReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      jobId,
      note,
    }: {
      jobId: string;
      note: string;
    }) => {
      // api.post<T> returns T directly, throws ApiError on failure
      return await api.post<{ job: JobDetailData }>(`/api/jobs/${jobId}/note`, {
        note,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job'] });
    },
  });

  const addNote = useCallback(
    async (jobId: string, note: string) => {
      await mutation.mutateAsync({ jobId, note });
    },
    [mutation]
  );

  return {
    addNote,
    isAdding: mutation.isPending,
    error: mutation.error as Error | null,
  };
}

/**
 * Return type for useJobAssign hook
 */
interface UseJobAssignReturn {
  assign: (jobId: string, bayId?: string | null, staffId?: string | null) => Promise<void>;
  isAssigning: boolean;
  error: Error | null;
}

/**
 * Hook for assigning job to bay/staff
 */
export function useJobAssign(): UseJobAssignReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      jobId,
      bayId,
      staffId,
    }: {
      jobId: string;
      bayId?: string | null;
      staffId?: string | null;
    }) => {
      // api.post<T> returns T directly, throws ApiError on failure
      return await api.post<{ job: JobDetailData }>(`/api/jobs/${jobId}/assign`, {
        bayId,
        staffId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  const assign = useCallback(
    async (jobId: string, bayId?: string | null, staffId?: string | null) => {
      await mutation.mutateAsync({ jobId, bayId, staffId });
    },
    [mutation]
  );

  return {
    assign,
    isAssigning: mutation.isPending,
    error: mutation.error as Error | null,
  };
}

// ============================================================================
// Estimate Line Items
// ============================================================================

export interface EstimateLineItemData {
  id?: string;
  description: string;
  category: string;
  quantity: number;
  unitPrice: number;
  totalPrice?: number;
  sortOrder?: number;
}

interface EstimateLinesData {
  estimate: unknown;
  lineItems: EstimateLineItemData[];
}

export function useEstimateLines(jobId: string | null) {
  return useQuery({
    queryKey: ['job', jobId, 'estimate-lines'],
    queryFn: () => api.get<EstimateLinesData>(`/api/jobs/${jobId}/estimate-lines`),
    enabled: !!jobId,
  });
}

export function useSaveEstimateLines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      lineItems,
      notes,
    }: {
      jobId: string;
      lineItems: Array<{ description: string; category: string; quantity: number; unitPrice: number }>;
      notes?: string;
    }) => {
      return await api.put<{ totalAmount: number; lineItemCount: number }>(
        `/api/jobs/${jobId}/estimate-lines`,
        { lineItems, notes }
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId, 'estimate-lines'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

// ============================================================================
// Resend Email
// ============================================================================

export function useResendJobEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      return await api.post<{ templateId: string; method: string }>(`/api/jobs/${jobId}/resend-email`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job'] });
    },
  });
}

/**
 * Return type for useJobStatusCounts hook
 */
interface UseJobStatusCountsReturn {
  counts: Record<string, number>;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Hook for fetching job status counts
 */
export function useJobStatusCounts(): UseJobStatusCountsReturn {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['jobs', 'status-counts'],
    queryFn: async () => {
      // api.get<T> returns T directly, throws ApiError on failure
      return await api.get<Record<string, number>>('/api/jobs/status-counts');
    },
    staleTime: 30000,
    refetchInterval: 60000,
  });

  return {
    counts: data || {},
    isLoading,
    refetch,
  };
}
